import React, { use, useEffect, useMemo, useRef, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useLocation } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor, getter } from '@progress/kendo-data-query';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import BulkUpdateDialog from '@components-clean/core/bulk-update-dialog/bulk-update-dialog';
import { ReviewFieldsType } from '@components-clean/core/bulk-update-dialog/field-components/risk-review-field';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { SelectedFieldValuesProps } from '@interfaces-clean/bulk-update.interface';
import { ExtendedCompleteRiskFieldsControl } from '@pages-clean/risk-assessment/risk-log/constants';
import AddControlDialog, {
    RiskControlFormInputs,
} from '@pages-clean/risk-assessment/risk-log/dialogs/add-control-dialog';
import AssignControlsToRiskDialog from '@pages-clean/risk-assessment/risk-log/dialogs/assign-controls-dialog';
import UnassignControlDialog from '@pages-clean/risk-assessment/risk-log/dialogs/unassign-control-dialog';
import { getAssignableControls } from '@pages-clean/risk-assessment/risk-log/helper';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { setSessionStorageActivePageTab } from '@utils-clean/page-tab-helpers';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    PhaseType,
    NamedItemType,
    TimeFrequencyType,
} from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import {
    convertReviewFrequency,
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromLocal,
} from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_NAMED_ITEMS_BY_TYPE, GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import {
    getNamedItemsByType,
    getNamedItemsByTypeVariables,
    getNamedItemsByTypeForStudy,
    getNamedItemsByTypeForStudyVariables,
} from '@queries/generated/common.generated';
import {
    getCompleteRisk_OpraQueries_risk_Risk_controls_StudyControl,
    getLibraryControls,
    getLibraryControlsVariables,
    getLibraryRisks,
    getLibraryRisksVariables,
    getStudyControls,
    getStudyControlsVariables,
} from '@queries/generated/legacy-risk-assessment.generated';
import {
    createControlAndAssignToRisk,
    createControlAndAssignToRiskVariables,
    updateControl,
    updateControlVariables,
    assignStudyAndLibraryControls,
    assignStudyAndLibraryControlsVariables,
    bulkUpdateControls,
    bulkUpdateControlsVariables,
} from '@queries/generated/risk-assessment.generated';
import { GET_LIBRARY_CONTROLS, GET_STUDY_CONTROLS } from '@queries/legacy-risk-assessment';
import {
    ASSIGN_STUDY_AND_LIBRARY_CONTROLS,
    BULK_UPDATE_CONTROLS,
    CREATE_CONTROL_AND_ASSIGN_TO_RISK,
    GET_COMPLETE_RISKS_RISK_LOG,
    GET_LIBRARY_RISKS,
    UPDATE_CONTROL,
} from '@queries/risk-assessment';

const ControlsArea = () => {
    const user = useAuthorization();
    const canView = user?.canRead?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });
    if (!canView) return null;

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    /* eslint-disable @eslint-react/rules-of-hooks */
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tGrid } = useTranslation('ram', { keyPrefix: 'riskLog.grids.controls' });
    const { t: tCommon } = useTranslation('common');
    const { TooltipCell, ActiveInactiveCell } = createGridCustomCells();
    const { current } = use(RiskLogContext);
    const [selectedControl, setSelectedControl] = useState<ExtendedCompleteRiskFieldsControl>();
    const location = useLocation();
    const createdOrUpdatedControlIdRef = useRef<string | null>(null);
    const addControlActionRef = useRef<boolean>(false);
    const showAddControlDialog = useBoolean(false);
    const showUnassignControlDialog = useBoolean(false);
    const showBulkUpdateDialog = useBoolean(false);
    const showInactivateControlDialog = useBoolean(false);

    const [pendingBulkUpdateVariables, setPendingBulkUpdateVariables] = useState<bulkUpdateControlsVariables | null>(
        null,
    );
    const [bulkUpdateRows, setBulkUpdateRows] = useState<Array<{ id: string }>>([]);
    const [selectedControlsCount, setSelectedControlsCount] = useState<number>(0);

    const [controlsSelectedState, setControlsSelectedState] = useState<{ [id: string]: boolean | number[] }>({});

    const showAssignControlsDialog = useBoolean(false);
    const keepAssignControlsDialogOpen = useBoolean(false);

    const [createControlMutation] = useMutation<createControlAndAssignToRisk, createControlAndAssignToRiskVariables>(
        CREATE_CONTROL_AND_ASSIGN_TO_RISK,
    );
    const [updateControlMutation] = useMutation<updateControl, updateControlVariables>(UPDATE_CONTROL);

    const [bulkUpdateControlsMutation] = useMutation<bulkUpdateControls, bulkUpdateControlsVariables>(
        BULK_UPDATE_CONTROLS,
    );

    const [assignStudyAndLibraryControlsMutation, { loading: assigningStudyAndLibraryControls }] = useMutation<
        assignStudyAndLibraryControls,
        assignStudyAndLibraryControlsVariables
    >(ASSIGN_STUDY_AND_LIBRARY_CONTROLS);

    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const clearFilters = () => {
        setFilterState({ logic: 'and', filters: [] });
    };

    const clearControlsSelection = () => {
        setSelectedControlsCount(0);
        setBulkUpdateRows([]);
        setControlsSelectedState({});
        setSelectedControl(undefined);
    };

    const riskId = current.selectionState.selectedItem?.id;
    const studyId = current?.study?.id as string | undefined;
    const organisationId = current?.study?.organisation?.id as string | undefined;
    const selectedCount = current?.selectionState?.selectedItemsCount ?? 0;
    const multiSelected = selectedCount > 1;

    const riskRecordId = current?.selectionState?.selectedItem?.recordId ?? null;
    const controls = current.selectionState.selectedItem?.controls;

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setSelectedControlsCount(0);
        setBulkUpdateRows([]);
        setControlsSelectedState({});
        setSelectedControl(undefined);
        /* eslint-enable @eslint-react/set-state-in-effect */
    }, [riskId]);

    const { data: studyControlsData, loading: loadingStudyControls } = useQuery<
        getStudyControls,
        getStudyControlsVariables
    >(GET_STUDY_CONTROLS, {
        variables: { studyId },
    });

    const { data: libraryControlsData, loading: loadingLibraryControls } = useQuery<
        getLibraryControls,
        getLibraryControlsVariables
    >(GET_LIBRARY_CONTROLS, {
        variables: { organisationId, includeSystemControls: false },
    });

    const { data: libraryRisksData, loading: loadingLibraryRisks } = useQuery<
        getLibraryRisks,
        getLibraryRisksVariables
    >(GET_LIBRARY_RISKS, {
        variables: { organisationId, includeSystemRisks: false },
    });

    const { data: controlPhasesData, loading: loadingControlPhases } = useQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROL_PHASE },
    });

    const { data: controlTypesData, loading: loadingControlTypes } = useQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROL_TYPE },
    });

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } = useQuery<
        getNamedItemsByTypeForStudy,
        getNamedItemsByTypeForStudyVariables
    >(GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY, {
        variables: {
            studyId,
            type: NamedItemType.RESPONSIBLE_FUNCTION,
        },
    });

    const assignableControls = React.useMemo(() => {
        if (loadingStudyControls || loadingLibraryControls || loadingLibraryRisks) return [];
        return (
            getAssignableControls({
                studyControls: studyControlsData,
                libraryControls: libraryControlsData,
                libraryRisks: libraryRisksData,
                studyRisk: current.selectionState.selectedItem,
            }) ?? []
        );
    }, [
        studyControlsData,
        loadingStudyControls,
        libraryControlsData,
        loadingLibraryControls,
        libraryRisksData,
        loadingLibraryRisks,
        current.selectionState.selectedItem,
    ]);

    const formatControlData = (items: getCompleteRisk_OpraQueries_risk_Risk_controls_StudyControl[]) => {
        const sortedItems = items.sort((a, b) => {
            const aInactive = a.inactiveDate ? new Date(a.inactiveDate).getTime() : null;
            const bInactive = b.inactiveDate ? new Date(b.inactiveDate).getTime() : null;

            if (aInactive && bInactive) return new Date(b.updated).getTime() - new Date(a.updated).getTime();
            if (aInactive) return 1;
            if (bInactive) return -1;

            return new Date(b.updated).getTime() - new Date(a.updated).getTime();
        });

        return sortedItems.map((item) => ({
            ...item,
            createdBy: item.createdBy ? `${item.createdBy.firstName} ${item.createdBy.lastName}` : '',
            updatedBy: item.updatedBy ? `${item.updatedBy.firstName} ${item.updatedBy.lastName}` : '',
            convertedOwner: item.owner
                ? { name: `${item.owner.firstName} ${item.owner.lastName}`, active: item.owner.active }
                : { name: '', active: true },
            reviewFrequency: convertReviewFrequency(
                item.reviewFrequencyPeriod ?? 0,
                TimeFrequencyType[item.reviewFrequencyUnits ?? 'NONE'],
            ),
            riskRecordId: current.selectionState.selectedItem?.recordId ?? '',
            riskStatement: current.selectionState.selectedItem?.statement ?? '',
        }));
    };

    const flattenedControls: ExtendedCompleteRiskFieldsControl[] = useMemo(
        () => (controls ? formatControlData(controls.filter((control) => control !== null)) : []),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [controls, current.selectionState.selectedItem],
    );

    const onControlsSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        setControlsSelectedState(selectedState);

        const idGetter = getter('id');

        if (Object.keys(selectedState).length !== 0 && idGetter) {
            const selectedRows = flattenedControls.filter((x) => selectedState[idGetter(x) as string] === true);
            setSelectedControlsCount(selectedRows.length);
            setBulkUpdateRows(selectedRows.map((item) => ({ id: idGetter(item) as string })));
            setSelectedControl(selectedRows[0]);
            return;
        }

        clearControlsSelection();
    };

    const onBulkUpdateClicked = () => {
        if (!canEdit) return;
        if (bulkUpdateRows.length < 1) return;
        showBulkUpdateDialog.setTrue();
    };

    const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

    const getIdFromDialogValue = (v: unknown) => {
        if (v == null) return null;

        if (typeof v === 'string') {
            return v.trim();
        }

        if (!isRecord(v)) return null;

        const { ref } = v as Record<string, unknown>;
        if (typeof ref === 'string') {
            return ref.trim();
        }

        const { id } = v as Record<string, unknown>;
        if (typeof id === 'string') {
            return id.trim();
        }

        return null;
    };

    const toLovMutationValue = (v: unknown) => {
        const id = getIdFromDialogValue(v);
        if (!id) return Constants.ObjectId.Empty;
        return id;
    };

    const toDateOnlyString = (v: unknown): string | null | undefined => {
        if (v === undefined) return undefined;
        if (v === null) return null;

        if (typeof v === 'string') {
            if (v.trim() === '') return null;
            return stringDateWithoutTimeFromLocal(v);
        }

        if (v instanceof Date && !Number.isNaN(v.getTime())) {
            return stringDateWithoutTimeFromLocal(v);
        }

        return null;
    };

    const isTimeFrequencyType = (v: string): v is TimeFrequencyType => {
        return (Object.values(TimeFrequencyType) as string[]).includes(v);
    };

    const toTimeFrequencyType = (v: unknown): TimeFrequencyType | null => {
        const id = getIdFromDialogValue(v);
        if (!id) return null;

        if (isTimeFrequencyType(id)) return id;

        const maybe = TimeFrequencyType[id as keyof typeof TimeFrequencyType];
        return maybe || null;
    };

    const onSubmitBulkUpdateControls = async (data: SelectedFieldValuesProps[]) => {
        if (!studyId) return;

        const controlIds = bulkUpdateRows.map((x) => x.id).filter((x) => x && x.trim().length > 0);
        if (controlIds.length < 1) return;

        const variables: bulkUpdateControlsVariables = {
            studyId,
            controlIds,
        };

        data.forEach((item) => {
            const raw: unknown = item.value;

            switch (item.field) {
                case 'actionToBeTaken':
                    if (typeof raw === 'string') variables.actionToBeTaken = raw;
                    else if (raw == null) variables.actionToBeTaken = null;
                    break;

                case 'additionalInformation':
                    if (typeof raw === 'string') variables.additionalInformation = raw;
                    else if (raw == null) variables.additionalInformation = null;
                    break;

                case 'purpose':
                    if (typeof raw === 'string') variables.purpose = raw;
                    else if (raw == null) variables.purpose = null;
                    break;

                case 'volume':
                    if (typeof raw === 'string') variables.volume = raw;
                    else if (raw == null) variables.volume = null;
                    break;

                case 'threshold':
                    if (typeof raw === 'string') variables.threshold = raw;
                    else if (raw == null) variables.threshold = null;
                    break;

                case 'activeDate': {
                    const value = toDateOnlyString(raw);
                    if (value !== undefined) variables.activeDate = value;
                    break;
                }

                case 'dateIdentified': {
                    const value = toDateOnlyString(raw);
                    if (value !== undefined) variables.dateIdentified = value;
                    break;
                }

                case 'inactiveDate': {
                    const value = toDateOnlyString(raw);
                    if (value !== undefined) variables.inactiveDate = value;
                    break;
                }

                case 'owner':
                    variables.ownerId = toLovMutationValue(raw);
                    break;

                case 'responsibleFunction':
                    variables.responsibleFunctionId = toLovMutationValue(raw);
                    break;

                case 'phase':
                    variables.controlPhaseId = toLovMutationValue(raw);
                    break;

                case 'type':
                    variables.typeId = toLovMutationValue(raw);
                    break;

                case 'frequency': {
                    if (!raw || !isRecord(raw)) break;

                    const review = raw as ReviewFieldsType;
                    const periodValue = review.reviewFrequencyPeriod;

                    variables.reviewFrequencyPeriod = typeof periodValue === 'number' ? periodValue : null;
                    variables.reviewFrequencyUnits = toTimeFrequencyType(review.reviewFrequencyUnit);
                    break;
                }

                default:
                    break;
            }
        });

        const shouldConfirmInactivation =
            typeof variables.inactiveDate === 'string' && variables.inactiveDate.trim().length > 0;

        if (shouldConfirmInactivation) {
            setPendingBulkUpdateVariables(variables);
            showInactivateControlDialog.setTrue();
            return;
        }

        try {
            const response = await bulkUpdateControlsMutation({
                variables,
                refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId } }],
            });

            if (!response?.data || response.data.bulkUpdateControls !== true) {
                ShowError(tCommon('errorMessage'));
                return;
            }

            showBulkUpdateDialog.setFalse();
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const onSubmitUnassignControl = (result: string) => {
        if (result === 'success') ShowSuccess(tCommon('actionsSuccessfulToast'));
        else ShowError(tCommon('errorMessage'));
        showUnassignControlDialog.setFalse();
    };

    useEffect(() => {
        if (createdOrUpdatedControlIdRef.current) {
            const updatedControl = flattenedControls.find(
                (control) => control.id === createdOrUpdatedControlIdRef.current,
            );
            if (updatedControl) {
                setSelectedControl(updatedControl);
                createdOrUpdatedControlIdRef.current = null;
            }
        }
    }, [flattenedControls]);
    /* eslint-enable @eslint-react/rules-of-hooks */

    const exportDate = stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY');
    const exportFilename = `${riskRecordId ?? ''}-Controls_${current?.study?.studyNumber ?? ''}_${exportDate}`;

    const onAddControlOpen = () => {
        setSessionStorageActivePageTab(location.key, 0);
        addControlActionRef.current = true;
        showAssignControlsDialog.setFalse();
        keepAssignControlsDialogOpen.setFalse();
        showAddControlDialog.setTrue();
    };

    const onSubmitAssignControls = async (
        studyControlIds: string[],
        libraryControlIds: string[],
        closeDialog: boolean,
    ) => {
        if (!riskId) return;

        assignStudyAndLibraryControlsMutation({
            variables: {
                id: riskId,
                studyControlIds,
                libraryControlIds,
            },
            refetchQueries: [
                {
                    query: GET_COMPLETE_RISKS_RISK_LOG,
                    variables: { studyId },
                },
                {
                    query: GET_STUDY_CONTROLS,
                    variables: { studyId },
                },
                {
                    query: GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY,
                    variables: {
                        studyId,
                        type: NamedItemType.RESPONSIBLE_FUNCTION,
                    },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data || !response.data?.assignStudyAndLibraryControls) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                setFilterState({
                    logic: 'and',
                    filters: [],
                });

                if (closeDialog) {
                    showAddControlDialog.setFalse();
                    showAssignControlsDialog.setFalse();
                    keepAssignControlsDialogOpen.setFalse();
                } else {
                    showAddControlDialog.setFalse();
                    showAssignControlsDialog.setTrue();
                    keepAssignControlsDialogOpen.setTrue();
                }

                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const handleAddControl = () => {
        addControlActionRef.current = true;
        setSessionStorageActivePageTab(location.key, 0);
        showAssignControlsDialog.setTrue();
    };

    const onSubmitAddControl = async (addControlData: RiskControlFormInputs) => {
        createControlMutation({
            variables: {
                control: {
                    studyId,
                    responsibleFunctionId: addControlData.responsibleFunction?.id,
                    ownerId: addControlData.owner?.id,
                    typeId: addControlData.type?.id,
                    description: addControlData.description,
                    actionToBeTaken: addControlData.actionToBeTaken,
                    activeDate: addControlData.activeDate
                        ? stringDateWithoutTimeFromLocal(addControlData.activeDate)
                        : null,
                    inactiveDate: addControlData.inactiveDate
                        ? stringDateWithoutTimeFromLocal(addControlData.inactiveDate)
                        : null,
                    purpose: addControlData.purpose,
                    volume: addControlData.volume,
                    additionalInformation: addControlData.additionalInformation,
                    reviewFrequencyPeriod: addControlData.reviewFrequencyPeriod,
                    reviewFrequencyUnits:
                        TimeFrequencyType[addControlData.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType],
                    phase: PhaseType[addControlData.phase?.id as keyof typeof PhaseType],
                    dateIdentified: addControlData.dateIdentified
                        ? stringDateWithoutTimeFromLocal(addControlData.dateIdentified)
                        : null,
                    threshold: addControlData.threshold,
                    controlPhaseId: addControlData.phase?.id,
                },
                riskId,
            },
            refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId } }],
        })
            .then((response) => {
                if (!response?.data || !response.data?.createControlAndAssignToRisk) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                createdOrUpdatedControlIdRef.current = response.data?.createControlAndAssignToRisk?.id ?? null;
                clearFilters();
                showAddControlDialog.setFalse();
                showAssignControlsDialog.setFalse();
                keepAssignControlsDialogOpen.setFalse();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => ShowError(tCommon('errorMessage')));
    };

    const onSubmitEditControl = async (editControlData: RiskControlFormInputs) => {
        updateControlMutation({
            variables: {
                control: {
                    id: addControlActionRef.current ? undefined : selectedControl?.id,
                    studyId,
                    responsibleFunctionId: editControlData.responsibleFunction?.id,
                    ownerId: editControlData.owner?.id,
                    typeId: editControlData.type?.id,
                    description: editControlData.description,
                    actionToBeTaken: editControlData.actionToBeTaken,
                    activeDate: editControlData.activeDate
                        ? stringDateWithoutTimeFromLocal(editControlData.activeDate)
                        : null,
                    inactiveDate: editControlData.inactiveDate
                        ? stringDateWithoutTimeFromLocal(editControlData.inactiveDate)
                        : null,
                    purpose: editControlData.purpose,
                    volume: editControlData.volume,
                    additionalInformation: editControlData.additionalInformation,
                    reviewFrequencyPeriod: editControlData.reviewFrequencyPeriod,
                    reviewFrequencyUnits:
                        TimeFrequencyType[editControlData.reviewFrequencyUnit?.id as keyof typeof TimeFrequencyType],
                    phase: PhaseType[editControlData.phase?.id as keyof typeof PhaseType],
                    dateIdentified: editControlData.dateIdentified
                        ? stringDateWithoutTimeFromLocal(editControlData.dateIdentified)
                        : null,
                    threshold: editControlData.threshold,
                    controlPhaseId: editControlData.phase?.id,
                },
            },
            refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId } }],
        })
            .then((response) => {
                if (!response?.data || !response.data?.updateControl) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                createdOrUpdatedControlIdRef.current = response.data?.updateControl?.id ?? null;
                clearFilters();
                showAddControlDialog.setFalse();
                showAssignControlsDialog.setFalse();
                keepAssignControlsDialogOpen.setFalse();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => ShowError(tCommon('errorMessage')));
    };

    const columns: GridColumnProps[] = [
        {
            id: 'status',
            field: 'status',
            title: tGrid('columns.status'),
            lockedByDefault: true,
            width: 1,
            minWidth: 120,
            lovData: [t('shared.active'), t('shared.inactive')],
        },
        {
            id: 'riskId',
            field: 'riskRecordId',
            title: tGrid('columns.riskId'),
            lockedByDefault: true,
            width: 1,
            minWidth: 120,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
            alwaysExportColumn: true,
            showByDefault: false,
        },
        {
            id: 'riskStatement',
            field: 'riskStatement',
            title: tGrid('columns.riskStatement'),
            lockedByDefault: true,
            width: 1,
            minWidth: 250,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
            alwaysExportColumn: true,
            showByDefault: false,
        },
        {
            id: 'description',
            field: 'description',
            title: tGrid('columns.description'),
            lockedByDefault: true,
            width: 1,
            minWidth: 250,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'controlPhase',
            field: 'controlPhase',
            title: tGrid('columns.phase'),
            width: 1,
            minWidth: 200,
            filter: 'text',
            filterKey: 'name',
            lovData: controlPhasesData?.namedItemsByType
                ? controlPhasesData.namedItemsByType
                      .map((item) => item?.name as string)
                      .sort((a, b) => a.localeCompare(b))
                : [],
            lovFilterContains: true,
            cells: {
                data: ActiveInactiveCell({
                    inactive: (n: INamedItem) => n?.active === false,
                    renderValue: (n: INamedItem) => n?.name,
                }),
            },
        },
        {
            id: 'dateIdentified',
            field: 'dateIdentified',
            title: tGrid('columns.dateIdentified'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 120,
            cellName: 'DateCell',
        },
        {
            id: 'responsibleFunction',
            field: 'responsibleFunction',
            title: tGrid('columns.responsibleFunction'),
            width: 1,
            minWidth: 200,
            filter: 'text',
            filterKey: 'name',
            lovData: responsibleFunctionsData?.namedItemsByTypeForStudy
                ? responsibleFunctionsData.namedItemsByTypeForStudy
                      .map((item) => item?.name as string)
                      .sort((a, b) => a.localeCompare(b))
                : [],
            lovFilterContains: true,
            cells: {
                data: ActiveInactiveCell({
                    inactive: (v: INamedItem) => v?.active === false,
                    renderValue: (v: INamedItem) => v?.name,
                }),
            },
            showByDefault: true,
        },
        {
            id: 'type',
            field: 'type',
            title: tGrid('columns.type'),
            width: 1,
            minWidth: 200,
            filter: 'text',
            filterKey: 'name',
            lovData: controlTypesData?.namedItemsByType
                ? controlTypesData.namedItemsByType
                      .map((item) => item?.name as string)
                      .sort((a, b) => a.localeCompare(b))
                : [],
            lovFilterContains: true,
            cells: {
                data: ActiveInactiveCell({
                    inactive: (n: INamedItem) => n?.active === false,
                    renderValue: (n: INamedItem) => n?.name,
                }),
            },
        },
        {
            id: 'owner',
            field: 'convertedOwner',
            title: tGrid('columns.owner'),
            width: 1,
            minWidth: 200,
            filterKey: 'name',
            filter: 'text',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'threshold',
            field: 'threshold',
            title: tGrid('columns.threshold'),
            width: 1,
            minWidth: 250,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'actionToBeTaken',
            field: 'actionToBeTaken',
            title: tGrid('columns.actionToBeTaken'),
            width: 1,
            minWidth: 250,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'reviewFrequency',
            field: 'reviewFrequency',
            title: tGrid('columns.reviewFrequency'),
            width: 1,
            minWidth: 150,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'activeDate',
            field: 'activeDate',
            title: tGrid('columns.activeDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 120,
            cellName: 'DateCell',
        },
        {
            id: 'inactiveDate',
            field: 'inactiveDate',
            title: tGrid('columns.inactiveDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 120,
            cellName: 'DateCell',
        },
        {
            id: 'monitoringGroup',
            field: 'monitoringGroup',
            title: tGrid('columns.groups.monitoring'),
            locked: false,
            sortable: false,
            enhancedGroupedColumnToggling: true,
            children: [
                {
                    id: 'purpose',
                    field: 'purpose',
                    title: tGrid('columns.monitoringPurpose'),
                    width: 1,
                    minWidth: 250,
                    filter: 'text',
                    cells: { data: TooltipCell({ isCsv: false }) },
                },
                {
                    id: 'volume',
                    field: 'volume',
                    title: tGrid('columns.monitoringVolume'),
                    width: 1,
                    minWidth: 250,
                    filter: 'text',
                    cells: { data: TooltipCell({ isCsv: false }) },
                },
                {
                    id: 'additionalInformation',
                    field: 'additionalInformation',
                    title: tGrid('columns.additionalInformation'),
                    width: 1,
                    minWidth: 250,
                    filter: 'text',
                    cells: { data: TooltipCell({ isCsv: false }) },
                },
            ],
        },
        {
            id: 'createdBy',
            field: 'createdBy',
            title: tGrid('columns.createdBy'),
            width: 1,
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'created',
            field: 'created',
            title: tGrid('columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 120,
            cellName: 'DateCell',
            showByDefault: false,
        },
        { id: 'updatedBy', field: 'updatedBy', title: tGrid('columns.updatedBy'), width: 1, minWidth: 150 },
        {
            id: 'updated',
            field: 'updated',
            title: tGrid('columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 120,
            cellName: 'DateCell',
        },
        {
            id: 'recordId',
            field: 'recordId',
            width: 1,
            minWidth: 120,
            showByDefault: false,
            title: tGrid('columns.id'),
        },
    ];

    if (multiSelected)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={tGrid('info.multipleParentRecords')} layout="center" />
            </div>
        );

    if (selectedCount === 0)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={tGrid('info.noParentRecordSelected')} layout="center" />
            </div>
        );

    if (!studyId || !riskId) return <div />;

    const dataLoaded =
        !loadingStudyControls &&
        studyControlsData &&
        !loadingLibraryControls &&
        libraryControlsData &&
        !loadingLibraryRisks &&
        libraryRisksData &&
        !loadingResponsibleFunctions &&
        responsibleFunctionsData &&
        !loadingControlPhases &&
        controlPhasesData &&
        !loadingControlTypes &&
        controlTypesData;

    if (!dataLoaded) return <Loading />;

    return (
        <div style={{ height: '100%' }} className="remove-card-padding">
            <Grid
                excludeHiddenRowMenuActions
                title={tGrid('title')}
                maxHeight="100%"
                testDataId="ram-risklog-controls-grid"
                data={flattenedControls}
                columns={columns}
                useLoadingOverlay
                showAddIcon
                addCallback={handleAddControl}
                addRecordTooltip={tGrid('addRecordLabel')}
                scrollable="virtual"
                dataItemKey="id"
                rowMenuActionsWidth={58}
                filterable
                filter={filterState}
                filterListener={(newFilterState) => {
                    setFilterState(newFilterState);
                    if (newFilterState?.filters) {
                        clearControlsSelection();
                    }
                }}
                clearFilter={() => {
                    setFilterState({ logic: 'and', filters: [] });
                    clearControlsSelection();
                    return Promise.resolve();
                }}
                canEdit={canEdit}
                removeCardBorder
                selectable={{ enabled: true, mode: 'multiple', drag: true }}
                onSelectionChange={onControlsSelectionChange}
                selectedState={controlsSelectedState}
                showBulkUpdateIcon
                onBulkUpdateClicked={onBulkUpdateClicked}
                defaultSelectedItem={bulkUpdateRows.length > 1 ? undefined : (selectedControl ?? undefined)}
                rowMenuActions={[
                    {
                        id: 1,
                        label: tGrid('actions.edit'),
                        onClick: (dataItem) => {
                            const control = dataItem as ExtendedCompleteRiskFieldsControl;
                            setSelectedControl(control);
                            addControlActionRef.current = false;
                            setSessionStorageActivePageTab(location.key, 0);
                            showAddControlDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'] as IconProp,
                    },
                    {
                        id: 2,
                        label: tGrid('actions.unassign'),
                        onClick: (dataItem) => {
                            const control = dataItem as ExtendedCompleteRiskFieldsControl;
                            setSelectedControl(control);
                            showUnassignControlDialog.setTrue();
                        },
                        icon: ['fal', 'link-slash'] as IconProp,
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={exportFilename}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                persistentStorageKey={String(PersistentStorageKeys['risk-log-controls'])}
            />
            {showUnassignControlDialog.value && selectedControl && current?.selectionState?.selectedItem && (
                <UnassignControlDialog
                    onSubmit={onSubmitUnassignControl}
                    onClose={() => showUnassignControlDialog.setFalse()}
                    control={selectedControl as ExtendedCompleteRiskFieldsControl}
                    risk={current?.selectionState.selectedItem}
                />
            )}

            {(showAddControlDialog.value ||
                (!keepAssignControlsDialogOpen.value &&
                    showAssignControlsDialog.value &&
                    assignableControls.length === 0)) && (
                // TODO: review for ESLint 10 - eslint-disable-next-line react/jsx-indent
                <AddControlDialog
                    studyRef={studyId}
                    risk={current.selectionState.selectedItem}
                    riskControl={addControlActionRef.current ? undefined : selectedControl}
                    onSubmit={addControlActionRef.current ? onSubmitAddControl : onSubmitEditControl}
                    onClose={() => {
                        showAddControlDialog.setFalse();
                        showAssignControlsDialog.setFalse();
                        keepAssignControlsDialogOpen.setFalse();
                    }}
                />
            )}

            {showBulkUpdateDialog.value && studyId && (
                <BulkUpdateDialog
                    studyRef={studyId}
                    onClose={() => showBulkUpdateDialog.setFalse()}
                    onSubmit={onSubmitBulkUpdateControls}
                    dialogTitle={tGrid('bulkUpdate.title')}
                    warningMessage={tGrid('bulkUpdate.warningMessage', { count: selectedControlsCount })}
                    fields={[
                        { field: 'actionToBeTaken', type: 'text', required: false, textLimit: 3000 },
                        { field: 'activeDate', type: 'date', required: false, textLimit: 0 },
                        { field: 'additionalInformation', type: 'text', required: false, textLimit: 3000 },
                        { field: 'dateIdentified', type: 'date', required: false, textLimit: 0 },
                        { field: 'inactiveDate', type: 'date', required: false, textLimit: 0 },
                        { field: 'purpose', type: 'text', required: false, textLimit: 3000 },
                        { field: 'volume', type: 'text', required: false, textLimit: 3000 },
                        { field: 'owner', type: 'ownerLov', required: false, textLimit: 0 },
                        {
                            field: 'phase',
                            type: 'controlPhaseLov',
                            required: false,
                            textLimit: 0,
                            ignoreStudyRef: true,
                        },
                        { field: 'responsibleFunction', type: 'responsibleFunctionLov', required: false, textLimit: 0 },
                        {
                            field: 'frequency',
                            type: 'riskReviewFields',
                            required: true,
                            textLimit: 0,
                            componentProps: { hideReviewRequired: true },
                        },
                        { field: 'threshold', type: 'text', required: false, textLimit: 3000 },
                        { field: 'type', type: 'controlTypeLov', required: false, textLimit: 0, ignoreStudyRef: true },
                    ]}
                    unsavedChangesArea="general"
                />
            )}

            {showInactivateControlDialog.value && (
                <ConfirmationDialog
                    title={tGrid('inactiveDialog.title')}
                    dataTestId="ram-risklog-controls-bulkupdate-inactivate-control-dialog"
                    ignoreReadonlyCtx
                    primaryBtnDisabled={false}
                    onSubmit={async () => {
                        if (!pendingBulkUpdateVariables) return;

                        try {
                            const response = await bulkUpdateControlsMutation({
                                variables: pendingBulkUpdateVariables,
                                refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId } }],
                            });

                            if (!response?.data || response.data.bulkUpdateControls !== true) {
                                ShowError(tCommon('errorMessage'));
                                return;
                            }

                            showInactivateControlDialog.setFalse();
                            setPendingBulkUpdateVariables(null);

                            showBulkUpdateDialog.setFalse();
                            ShowSuccess(tCommon('actionsSuccessfulToast'));
                        } catch {
                            ShowError(tCommon('errorMessage'));
                        }
                    }}
                    onClose={() => {
                        showInactivateControlDialog.setFalse();
                        setPendingBulkUpdateVariables(null);
                    }}
                    primaryBtnLabel={tCommon('confirm', { defaultValue: 'Confirm' })}
                    secondaryBtnLabel={tCommon('cancel', { defaultValue: 'Cancel' })}
                    closeIcon
                    style={{ zIndex: 2147483647 }}
                >
                    {tGrid('inactiveDialog.body')}
                </ConfirmationDialog>
            )}

            {showAssignControlsDialog.value &&
                (assignableControls.length > 0 || keepAssignControlsDialogOpen.value) && (
                    <AssignControlsToRiskDialog
                        riskStatement={current.selectionState.selectedItem?.statement}
                        assignableControls={assignableControls}
                        assigning={assigningStudyAndLibraryControls || !dataLoaded}
                        onClose={() => {
                            showAddControlDialog.setFalse();
                            showAssignControlsDialog.setFalse();
                            keepAssignControlsDialogOpen.setFalse();
                        }}
                        onSubmit={onSubmitAssignControls}
                        onAddControlOpen={onAddControlOpen}
                    />
                )}
        </div>
    );
};

export default ControlsArea;
